/**
 * Refresh Stage 4: Competitor Synthesis
 *
 * LLM stage that synthesizes all competitor extractions into actionable
 * competitive intelligence. Same pattern as creation Stage2dAggregateV2.
 */

import React from 'react';
import { StagePanel } from '../StagePanel';
import { useArticleContext } from '../ArticleContext';
import type { ArticleDocument } from '../types';

function InputSummary() {
  const { document } = useArticleContext();
  const extractions = (document?.targeting?.competitors?.extractions || []) as any[];
  const serp = document?.targeting?.serp as any;

  return (
    <div className="space-y-2">
      <div className="text-xs text-gray-600 bg-gray-50 rounded border p-2">
        <span className="text-gray-400">Extractions:</span>{' '}
        {extractions.length > 0
          ? extractions.map(e => `${e.domain} (#${e.serp_position}, ${e.above_or_below_us})`).join(', ')
          : 'None yet'}
      </div>
      {serp?.overview?.decision && (
        <div className="text-xs text-gray-600 bg-gray-50 rounded border p-2">
          <span className="text-gray-400">SERP decision:</span> {serp.overview.decision}
        </div>
      )}
    </div>
  );
}

function renderOutput(doc: ArticleDocument) {
  const analysis = (doc.targeting?.competitors as any)?.analysis;
  if (!analysis) {
    return <div className="text-sm text-gray-500">No synthesis yet.</div>;
  }
  return (
    <div className="space-y-3">
      {analysis.table_stakes_topics?.length > 0 && (
        <div>
          <div className="text-xs font-medium text-gray-700 mb-1">Table stakes</div>
          <ul className="text-xs text-gray-600 list-disc pl-4">
            {analysis.table_stakes_topics.map((item: string, i: number) => <li key={i}>{item}</li>)}
          </ul>
        </div>
      )}
      {analysis.common_formats?.length > 0 && (
        <div>
          <div className="text-xs font-medium text-gray-700 mb-1">Common formats</div>
          <ul className="text-xs text-gray-600 list-disc pl-4">
            {analysis.common_formats.map((item: string, i: number) => <li key={i}>{item}</li>)}
          </ul>
        </div>
      )}
      {analysis.gaps_we_can_exploit?.length > 0 && (
        <div>
          <div className="text-xs font-medium text-gray-700 mb-1">Gaps we can exploit</div>
          <ul className="text-xs text-gray-600 list-disc pl-4">
            {analysis.gaps_we_can_exploit.map((item: string, i: number) => <li key={i}>{item}</li>)}
          </ul>
        </div>
      )}
      {analysis.recommended_word_count_range && (
        <div>
          <div className="text-xs font-medium text-gray-700 mb-1">Recommended word count</div>
          <div className="text-xs text-gray-600">
            {analysis.recommended_word_count_range.min}–{analysis.recommended_word_count_range.max} words
            {analysis.target_content_depth && <span className="text-gray-400 ml-2">({analysis.target_content_depth})</span>}
          </div>
        </div>
      )}
    </div>
  );
}

export function RefreshCompetitorSynthesizeV2() {
  const { document } = useArticleContext();
  const hasExtractions = ((document?.targeting?.competitors?.extractions || []) as any[]).length > 0;

  return (
    <StagePanel
      stageId="refresh_4_competitor_synthesize"
      isLlmStage
      title="Refresh: Competitor Synthesis"
      description="Synthesizes competitor extractions into table stakes, gaps, and word count targets."
      runDisabled={!hasExtractions}
      runDisabledReason={!hasExtractions ? 'Requires competitor extractions (Stage 3)' : undefined}
      renderOutput={renderOutput}
    >
      <InputSummary />
    </StagePanel>
  );
}
